// Schedule-domain HTTP service (Layer 2 — transport). Wraps lib/http.ts's
// request() for the Schedule endpoints used by pages/Schedule.tsx and
// pages/Meeting.tsx. Methods moved verbatim off ApiClient
// (apps/frontend/src/utils/api.ts), which now delegates here to preserve
// every existing `apiClient.*` call site.
import { request } from '@/lib/http'
import type { Schedule, ScheduleParticipant } from '@/types'

export const scheduleApi = {
  list(filters?: {
    start_date?: string
    end_date?: string
    status?: string
    project_id?: number
    team_id?: number
  }): Promise<{ schedules: Schedule[]; total: number }> {
    const queryParams = new URLSearchParams()
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, value.toString())
        }
      })
    }
    const endpoint = `/schedules${queryParams.toString() ? '?' + queryParams.toString() : ''}`
    return request(endpoint)
  },

  create(scheduleData: {
    title: string
    description?: string
    date: string
    start_time: string
    end_time: string
    timezone?: string
    meeting_type?: 'in_person' | 'google_meet' | 'zoom' | 'teams'
    meeting_link?: string
    location?: string
    is_recurring?: boolean
    recurring_pattern?: string
    project_id?: number
    team_id?: number
    participants?: Array<{ user_id?: number; email?: string; name?: string }>
  }): Promise<{ message: string; schedule: Schedule }> {
    return request('/schedules', { method: 'POST', body: JSON.stringify(scheduleData) })
  },

  get(id: number): Promise<{ schedule: Schedule }> {
    return request(`/schedules/${id}`)
  },

  update(id: number, scheduleData: Partial<Schedule>): Promise<{ message: string; schedule: Schedule }> {
    return request(`/schedules/${id}`, { method: 'PUT', body: JSON.stringify(scheduleData) })
  },

  remove(id: number): Promise<{ message: string }> {
    return request(`/schedules/${id}`, { method: 'DELETE' })
  },

  addParticipant(scheduleId: number, participantData: { user_id?: number; email?: string; name?: string }): Promise<{ message: string; participant: ScheduleParticipant }> {
    return request(`/schedules/${scheduleId}/participants`, { method: 'POST', body: JSON.stringify(participantData) })
  },

  respond(scheduleId: number, participantId: number, responseStatus: 'accepted' | 'declined' | 'tentative'): Promise<{ message: string; participant: ScheduleParticipant }> {
    return request(`/schedules/${scheduleId}/participants/${participantId}/response`, {
      method: 'PUT',
      body: JSON.stringify({ response_status: responseStatus }),
    })
  },
}
